import { IMsalContext } from "@azure/msal-react";
import { Exchange } from "../../Services/ExchangeSelectorContextProvider";
import { ProductType } from "../../Services/nats/contracts";
import { BalancingGroup } from "../configTab/balancingGroup";

/** A plain limit order: side, price and quantity, with no working behaviour. */
export type LimitOrderConfiguration = {
  direction: Direction;
  price: number | null;
  quantity: number | null;
  product_name: string;
  text?: string;
  tag: string;
  initials: string;
  /**
   * Which of our balancing groups the order goes under — JEPX's `location`.
   * Chosen on create; every update sends back the value the hub echoed, never
   * a new one (`withEchoedBalancingGroup`). `null` off the hub for algos
   * created before the field existed, and then omitted from the update.
   */
  balancing_group?: BalancingGroup | null;
};

export type Boundaries = [number | null, number | null];

/**
 * Trader-supplied margin override and the reference price behind it, carried by
 * `_shimurai` algos and `LinearCloser` terminators (see `configTab/algoSpec.ts`).
 *
 * Both are **prices in plain-decimal ¥/MWh**, the same unit `configuration.price`
 * uses — the hub echoes them back exactly as we sent them, with no
 * `price_delimiter` scaling. So build with `Price.fromYenPerMwh`, display
 * `.yenPerKwh`, and send `.yenPerMwh`; never let a raw number off one of these
 * fields reach the form or the wire.
 *
 * Optional on every configuration rather than split into its own variant: the
 * suffix is what marks a shimurai algo, and the hub is free to hang it off
 * either behaviour's config.
 */
export type ShimuraiExtraConfig = {
  margin_override?: number | null;
  reference_price?: number | null;
};

/**
 * The extra settings of a `LinearCloser` terminator. `min_margin` and
 * `max_margin` are prices in plain-decimal ¥/MWh, like
 * {@link ShimuraiExtraConfig}. `upper_omt_update_frequency` is in seconds, sent
 * as typed.
 */
export type LinearCloserExtraConfig = {
  min_margin?: number | null;
  max_margin?: number | null;
  upper_omt_update_frequency?: number | null;
};

/**
 * A `terminator` algo. Its own type rather than an extension of
 * {@link LimitOrderConfiguration}, so the two can diverge — Skynet's terminator
 * also carries `peak_quantity`, `soft_boundaries` and more. For now it is the
 * limit order's fields plus `behaviors`.
 */
export type TerminatorConfiguration = {
  direction: Direction;
  price: number | null;
  quantity: number | null;
  product_name: string;
  text?: string;
  tag: string;
  initials: string;
  /** As on {@link LimitOrderConfiguration}. */
  balancing_group?: BalancingGroup | null;
  /**
   * How the terminator works the order. One value, not a list, despite the
   * key. `string` off the wire — Skynet's terminator has more behaviours than
   * this desk offers — and narrowed with `isTerminatorBehavior`.
   */
  behaviors: string;
};

export type AlgoConfiguration = (
  LimitOrderConfiguration | TerminatorConfiguration
) &
  ShimuraiExtraConfig &
  LinearCloserExtraConfig;

export type ProductDefinition = {
  delivery_area_id: string;
  delivery_start: string;
  delivery_end: string;
  product_types: ProductType[];
};

export type AlgoData = {
  quantity?: {
    kilo_watts: number;
  };
  direction?: Direction;
  behavior?: string;
  information?: string;
  price?: {
    price: number;
  };
};

export type Algo<T> = {
  algo_id: string;
  configuration: T;
  product_definitions: ProductDefinition[]; // LT send all pdefs. We currently do not support multiple pdefs so only the first is used
  algo_spec: {
    /**
     * Open-ended on purpose. The hub also serves `nurser` and the `_shimurai`
     * variants, and a closed union here silently made those unreachable —
     * `behaviorFromAlgoSpec` and `isShimuraiAlgoSpec` do the narrowing instead.
     */
    name: string;
    version: string | undefined;
  };
  state: AlgoState;
  start?: string;
  end?: string;

  algo_data?: AlgoData;

  exchange: Exchange;
};

export enum Direction {
  BUY = "BUY",
  SELL = "SELL",
}
export const direction = (d: Direction | string | undefined): Direction => {
  if (!d) {
    return Direction.SELL;
  }
  const res = d.toUpperCase() as Direction;
  if (res != Direction.BUY && res != Direction.SELL) {
    throw new Error("Invalid direction: " + d);
  }
  return res;
};

export type Severity = "success" | "error" | "warning";

export type Notification = {
  message: string;
  severity: Severity;
  id: string;
};

export type Notify = (notification: Notification) => void;

export type AlgoState = {
  message: string;
  state:
    | "Running"
    | "ManuallyStopped"
    | "Stopped"
    | "Crashed"
    | "Scheduled"
    // Stopped by the exchange going into emergency mode; restarted when it
    // returns to normal mode.
    | "EmergencyStopped"
    | "NotRunning"
    | "Deleting";
  trade_quantity: undefined | number;
};

export type AlgoStateDebugMessage = {
  algo_id: string;
  algo_state: AlgoState;
};

export type GridProps = {
  msal: IMsalContext;
  algos: Algo<AlgoConfiguration>[];
};
